import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ChevronLeft, Plus, RefreshCw, X } from 'lucide-react';
import {
  apiClient,
  type ImportJob,
  type ImportJobStatus,
  type ImportKind,
  type ImportPreviewRow,
} from '@/lib/api-client';
import { Button } from '@/components/ui/button';
import { ImportUpload } from './components/import-upload';
import { StatusBadge, ImportProgress } from './components/import-job-status';
import { ImportPreviewTable } from './components/import-preview-table';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const KIND_LABELS: Record<ImportKind, string> = {
  clients: 'Pessoas',
  cases: 'Processos',
  tasks: 'Compromissos',
  financial: 'Financeiro',
};

const VALID_KINDS: ImportKind[] = ['clients', 'cases', 'tasks', 'financial'];

function isValidKind(k: string | undefined): k is ImportKind {
  return VALID_KINDS.includes(k as ImportKind);
}

function fmtDate(iso: string) {
  return new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const IN_PROGRESS: ImportJobStatus[] = ['pending', 'parsing', 'importing'];

// ---------------------------------------------------------------------------
// Modal shell simples
// ---------------------------------------------------------------------------

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}

function Modal({ open, onClose, title, children }: ModalProps) {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="relative w-full max-w-2xl rounded-xl border border-[var(--border)] bg-[var(--surface)] shadow-2xl">
        <div className="flex items-center justify-between border-b border-[var(--border)] px-6 py-4">
          <h2 className="font-semibold text-[var(--fg)]">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-[var(--fg-muted)] hover:text-[var(--fg)]"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Painel de detalhe do job
// ---------------------------------------------------------------------------

interface JobDetailPanelProps {
  job: ImportJob;
  onRefresh: () => void;
  onJobUpdated: (job: ImportJob) => void;
}

function JobDetailPanel({ job, onRefresh, onJobUpdated }: JobDetailPanelProps) {
  const [previewRows, setPreviewRows] = useState<ImportPreviewRow[] | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [runLoading, setRunLoading] = useState(false);
  const [toast, setToast] = useState<{ msg: string; kind: 'ok' | 'err' } | null>(null);

  function showToast(msg: string, kind: 'ok' | 'err' = 'ok') {
    setToast({ msg, kind });
    setTimeout(() => setToast(null), 4000);
  }

  async function handlePreview() {
    setPreviewLoading(true);
    try {
      const rows = await apiClient.importsPreview(job.id);
      setPreviewRows(rows);
    } catch (e) {
      const msg = (e as { message?: string }).message ?? 'Erro';
      if (msg.toLowerCase().includes('not implemented') || msg.toLowerCase().includes('não implementad')) {
        showToast('Importador deste tipo ainda em desenvolvimento.', 'err');
      } else {
        showToast(msg, 'err');
      }
    } finally {
      setPreviewLoading(false);
    }
  }

  async function handleRun() {
    if (!window.confirm('Confirma a importação? Esta ação não pode ser desfeita.')) return;
    setRunLoading(true);
    try {
      const updated = await apiClient.importsRun(job.id);
      onJobUpdated(updated);
      showToast('Importação concluída.', 'ok');
    } catch (e) {
      const msg = (e as { message?: string }).message ?? 'Erro';
      if (msg.toLowerCase().includes('not implemented') || msg.toLowerCase().includes('não implementad')) {
        showToast('Importador deste tipo ainda em desenvolvimento.', 'err');
      } else {
        showToast(msg, 'err');
      }
    } finally {
      setRunLoading(false);
    }
  }

  return (
    <div className="flex flex-col gap-4">
      {toast && (
        <div
          className={[
            'rounded-md px-3 py-2 text-xs',
            toast.kind === 'ok'
              ? 'bg-emerald-500/10 text-emerald-500'
              : 'bg-red-500/10 text-red-500',
          ].join(' ')}
        >
          {toast.msg}
        </div>
      )}

      <div className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
        <div className="flex flex-col gap-0.5">
          <span className="text-xs text-[var(--fg-muted)]">Status</span>
          <StatusBadge status={job.status} />
        </div>
        <div className="flex flex-col gap-0.5">
          <span className="text-xs text-[var(--fg-muted)]">Linhas</span>
          <span className="font-medium text-[var(--fg)]">
            {job.totalRows ?? '—'}
          </span>
        </div>
        <div className="flex flex-col gap-0.5">
          <span className="text-xs text-[var(--fg-muted)]">Criado em</span>
          <span className="font-medium text-[var(--fg)]">
            {fmtDate(job.createdAt)}
          </span>
        </div>
        <div className="flex flex-col gap-0.5">
          <span className="text-xs text-[var(--fg-muted)]">Arquivo</span>
          <span className="truncate font-medium text-[var(--fg)]" title={job.fileName}>
            {job.fileName}
          </span>
        </div>
      </div>

      {job.totalRows !== null && (
        <ImportProgress
          totalRows={job.totalRows}
          processedRows={job.processedRows}
          successRows={job.successRows}
          skippedRows={job.skippedRows}
          errorRows={job.errorRows}
        />
      )}

      {job.errorMessage && (
        <p className="rounded-md bg-red-500/10 px-3 py-2 text-xs text-red-500">
          {job.errorMessage}
        </p>
      )}

      {previewRows && (
        <div>
          <p className="mb-2 text-xs font-medium text-[var(--fg-muted)] uppercase tracking-wide">
            Prévia (primeiras {previewRows.length} linhas)
          </p>
          <ImportPreviewTable rows={previewRows} />
        </div>
      )}

      <div className="flex items-center gap-2">
        {job.status === 'preview' && (
          <>
            <Button
              variant="outline"
              size="sm"
              onClick={handlePreview}
              disabled={previewLoading}
            >
              {previewLoading ? 'Carregando…' : 'Prévia'}
            </Button>
            <Button
              size="sm"
              onClick={handleRun}
              disabled={runLoading}
            >
              {runLoading ? 'Importando…' : 'Executar importação'}
            </Button>
          </>
        )}
        <Button variant="outline" size="sm" onClick={onRefresh}>
          <RefreshCw className="h-3.5 w-3.5 mr-1" />
          Atualizar
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Página principal do kind
// ---------------------------------------------------------------------------

export default function ImportacaoKindPage() {
  const { kind } = useParams<{ kind: string }>();
  const navigate = useNavigate();

  const [jobs, setJobs] = useState<ImportJob[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState<ImportJob | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [toast, setToast] = useState<{ msg: string; kind: 'ok' | 'err' } | null>(null);
  const pageSize = 20;

  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  if (!isValidKind(kind)) {
    return (
      <div className="py-12 text-center text-sm text-[var(--fg-muted)]">
        Tipo de importação inválido.
      </div>
    );
  }

  const kindLabel = KIND_LABELS[kind];

  function showToast(msg: string, k: 'ok' | 'err' = 'ok') {
    setToast({ msg, kind: k });
    setTimeout(() => setToast(null), 4000);
  }

  const loadJobs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiClient.importsList({ kind, page, pageSize });
      setJobs(res.items);
      setTotal(res.total);
    } catch (e) {
      setError((e as { message?: string }).message ?? 'Erro ao carregar');
    } finally {
      setLoading(false);
    }
  }, [kind, page]);

  useEffect(() => {
    void loadJobs();
  }, [loadJobs]);

  // Poll a cada 5s se houver job em andamento
  useEffect(() => {
    const hasInProgress = jobs.some((j) => IN_PROGRESS.includes(j.status));
    if (hasInProgress) {
      pollRef.current = setInterval(() => void loadJobs(), 5000);
    }
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [jobs, loadJobs]);

  function handleUploadSuccess(jobId: string) {
    setModalOpen(false);
    showToast('Arquivo processado. Revise o job abaixo.');
    void loadJobs().then(() => {
      // Abre o detalhe do job recém-criado
      apiClient.importsGet(jobId).then((job) => {
        setSelectedJob(job);
        setDetailOpen(true);
      }).catch(() => { /* silencioso */ });
    });
  }

  async function handleCancel(job: ImportJob) {
    if (!window.confirm('Cancelar esta importação?')) return;
    try {
      await apiClient.importsCancel(job.id);
      showToast('Importação cancelada.');
      void loadJobs();
    } catch (e) {
      showToast((e as { message?: string }).message ?? 'Erro ao cancelar', 'err');
    }
  }

  const totalPages = Math.ceil(total / pageSize);

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-[var(--fg-muted)]">
        <button
          type="button"
          onClick={() => navigate('/escritorio/configuracoes/importacao')}
          className="flex items-center gap-1 hover:text-[var(--fg)]"
        >
          <ChevronLeft className="h-4 w-4" />
          Importação de dados
        </button>
        <span>/</span>
        <span className="text-[var(--fg)]">{kindLabel}</span>
      </div>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-[var(--fg)]">
            Importação de {kindLabel}
          </h1>
          <p className="mt-0.5 text-sm text-[var(--fg-muted)]">
            {total} importação{total !== 1 ? 'ões' : ''} registrada{total !== 1 ? 's' : ''}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {kind === 'financial' && (
            <Link
              to="/escritorio/financeiro/agrupar-importacoes"
              className="inline-flex items-center gap-1.5 rounded-md border border-[var(--border)] px-3 py-1.5 text-xs text-[var(--fg-muted)] hover:bg-[var(--accent)]/5 hover:text-[var(--fg)]"
              title="Consolidar parcelas importadas em contratos do financeiro"
            >
              Agrupar parcelas importadas
            </Link>
          )}
          <Button onClick={() => setModalOpen(true)}>
            <Plus className="mr-1.5 h-4 w-4" />
            Nova importação
          </Button>
        </div>
      </div>

      {toast && (
        <div
          className={[
            'rounded-md px-3 py-2 text-xs',
            toast.kind === 'ok'
              ? 'bg-emerald-500/10 text-emerald-500'
              : 'bg-red-500/10 text-red-500',
          ].join(' ')}
        >
          {toast.msg}
        </div>
      )}

      {/* Tabela de histórico */}
      <div className="overflow-hidden rounded-lg border border-[var(--border)] bg-[var(--surface)]">
        {loading ? (
          <p className="px-4 py-8 text-center text-sm text-[var(--fg-muted)]">
            Carregando…
          </p>
        ) : error ? (
          <p className="px-4 py-8 text-center text-sm text-red-500">{error}</p>
        ) : jobs.length === 0 ? (
          <div className="flex flex-col items-center gap-3 px-4 py-12 text-center">
            <p className="text-sm text-[var(--fg-muted)]">
              Nenhuma importação registrada. Clique em{' '}
              <strong>Nova importação</strong> para começar.
            </p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[color-mix(in_oklch,var(--fg)_4%,transparent)] text-left text-xs uppercase tracking-wide text-[var(--fg-muted)]">
              <tr>
                <th className="px-4 py-2 font-medium">Data</th>
                <th className="px-4 py-2 font-medium">Arquivo</th>
                <th className="px-4 py-2 font-medium">Status</th>
                <th className="px-4 py-2 font-medium">Linhas</th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody>
              {jobs.map((job) => (
                <tr
                  key={job.id}
                  className="border-t border-[var(--border)] transition hover:bg-[color-mix(in_oklch,var(--fg)_2%,transparent)]"
                >
                  <td className="px-4 py-2 text-[var(--fg-muted)] whitespace-nowrap">
                    {fmtDate(job.createdAt)}
                  </td>
                  <td className="px-4 py-2 max-w-[200px]">
                    <button
                      type="button"
                      className="truncate text-left text-[var(--fg)] hover:text-[var(--accent)] font-medium"
                      title={job.fileName}
                      onClick={() => {
                        setSelectedJob(job);
                        setDetailOpen(true);
                      }}
                    >
                      {job.fileName}
                    </button>
                  </td>
                  <td className="px-4 py-2">
                    <StatusBadge status={job.status} />
                  </td>
                  <td className="px-4 py-2 text-[var(--fg-muted)]">
                    {job.totalRows !== null ? (
                      <span>
                        {job.processedRows ?? 0}/{job.totalRows}
                        {job.errorRows !== null && job.errorRows > 0 && (
                          <span className="ml-1 text-red-400">
                            ({job.errorRows} erro{job.errorRows !== 1 ? 's' : ''})
                          </span>
                        )}
                      </span>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="px-4 py-2 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setSelectedJob(job);
                          setDetailOpen(true);
                        }}
                      >
                        Ver detalhe
                      </Button>
                      {IN_PROGRESS.includes(job.status) && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => void handleCancel(job)}
                          className="text-red-400 hover:text-red-500"
                        >
                          Cancelar
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Paginação */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 text-sm">
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
          >
            Anterior
          </Button>
          <span className="text-[var(--fg-muted)]">
            {page} / {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            Próxima
          </Button>
        </div>
      )}

      {/* Modal: nova importação */}
      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={`Nova importação — ${kindLabel}`}
      >
        <ImportUpload kind={kind} onSuccess={handleUploadSuccess} />
      </Modal>

      {/* Modal: detalhe do job */}
      <Modal
        open={detailOpen && selectedJob !== null}
        onClose={() => setDetailOpen(false)}
        title="Detalhe da importação"
      >
        {selectedJob && (
          <JobDetailPanel
            job={selectedJob}
            onRefresh={() => {
              void apiClient.importsGet(selectedJob.id).then((j) => setSelectedJob(j));
            }}
            onJobUpdated={(j) => {
              setSelectedJob(j);
              void loadJobs();
            }}
          />
        )}
      </Modal>
    </div>
  );
}
